import { createAtom, type Atom } from '@xstate/store';

import type { RovoStreamResponse, TaskStatus } from '../../api/convo-ai/SpeechToSpeechModel';

export type HerTask = {
	task_id: string;
	task_name: string;
	description: string;
	status: TaskStatus;
	result?: string;
	error?: string;
};

export type HerNotification = {
	id: string;
	message: string;
	icon?: 'info' | 'success' | 'warning';
	duration_ms?: number;
};

export type HerRovoMessage = {
	sourceCallId?: string;
	response: RovoStreamResponse;
};

export type HerVoiceState = 'inactive' | 'active' | 'listening' | 'working' | 'speaking' | 'error';

export type HerState = {
	/** Whether the HER container is visible */
	isHerActive: boolean;
	/** The current voice-session state shown by the constellation */
	voiceState: HerVoiceState;
	/** Whether the container is in expanded mode */
	isExpanded: boolean;
	/** Whether the wave animation is playing */
	isAnimating: boolean;
	/** The content ID associated with the HER session */
	contentId: string | null;
	/** The content type (page, blogpost, whiteboard) */
	contentType: string;
	/** The cloud ID for the Atlassian instance */
	cloudID: string;
	/** Live text preview accumulated from streamed ANSWER_PART events */
	textPreview: string;
	/** Active async tasks keyed by task_id */
	tasks: Record<string, HerTask>;
	/** Ephemeral white-style notifications keyed by id */
	notifications: Record<string, HerNotification>;
	/** The latest structured Rovo response to display in the HER toast */
	rovoMessage: HerRovoMessage | null;
};

const initialState: HerState = {
	isHerActive: false,
	voiceState: 'inactive',
	isExpanded: true,
	isAnimating: true,
	contentId: null,
	contentType: 'page',
	cloudID: '',
	textPreview: '',
	tasks: {},
	notifications: {},
	rovoMessage: null,
};

export const herState: Atom<HerState> = createAtom<HerState>(initialState);

export const toggleHer = (contentId?: string, cloudID?: string, contentType?: string): void => {
	herState.set((prevState) => ({
		...prevState,
		isHerActive: !prevState.isHerActive,
		voiceState: !prevState.isHerActive ? 'active' : 'inactive',
		// Reset to expanded when opening
		isExpanded: !prevState.isHerActive ? true : prevState.isExpanded,
		isAnimating: !prevState.isHerActive ? true : prevState.isAnimating,
		contentId: !prevState.isHerActive ? (contentId ?? null) : null,
		contentType: !prevState.isHerActive ? (contentType ?? 'page') : 'page',
		cloudID: !prevState.isHerActive ? (cloudID ?? prevState.cloudID) : prevState.cloudID,
		textPreview: !prevState.isHerActive ? '' : prevState.textPreview,
		rovoMessage: null,
	}));
};

export const openHer = (contentId?: string, cloudID?: string, contentType?: string): void => {
	herState.set((prevState) => ({
		...prevState,
		isHerActive: true,
		voiceState: 'active',
		isExpanded: true,
		isAnimating: true,
		contentId: contentId ?? null,
		contentType: contentType ?? 'page',
		cloudID: cloudID ?? prevState.cloudID,
		rovoMessage: null,
	}));
};

export const closeHer = (): void => {
	herState.set((prevState) => ({
		...prevState,
		isHerActive: false,
		voiceState: 'inactive',
		contentId: null,
		contentType: 'page',
		textPreview: '',
		rovoMessage: null,
	}));
};

export const setExpanded = (expanded: boolean): void => {
	herState.set((prevState) => ({
		...prevState,
		isExpanded: expanded,
	}));
};

export const setAnimating = (animating: boolean): void => {
	herState.set((prevState) => ({
		...prevState,
		isAnimating: animating,
	}));
};

export const setVoiceState = (voiceState: HerVoiceState): void => {
	herState.set((prevState) => ({
		...prevState,
		voiceState,
	}));
};

export const appendTextPreview = (text: string): void => {
	herState.set((prevState) => ({
		...prevState,
		textPreview: prevState.textPreview + text,
	}));
};

export const clearTextPreview = (): void => {
	herState.set((prevState) => ({
		...prevState,
		textPreview: '',
	}));
};

export const addTask = (task: HerTask): void => {
	herState.set((prevState) => ({
		...prevState,
		tasks: { ...prevState.tasks, [task.task_id]: task },
	}));
};

export const updateTask = (task: HerTask): void => {
	herState.set((prevState) => ({
		...prevState,
		tasks: { ...prevState.tasks, [task.task_id]: task },
	}));
};

export const removeTask = (taskId: string): void => {
	herState.set((prevState) => {
		const { [taskId]: _, ...rest } = prevState.tasks;
		return { ...prevState, tasks: rest };
	});
};

export const addNotification = (notification: HerNotification): void => {
	herState.set((prevState) => ({
		...prevState,
		notifications: { ...prevState.notifications, [notification.id]: notification },
	}));
};

export const removeNotification = (notificationId: string): void => {
	herState.set((prevState) => {
		const { [notificationId]: _, ...rest } = prevState.notifications;
		return { ...prevState, notifications: rest };
	});
};

export const setRovoMessage = (rovoMessage: HerRovoMessage): void => {
	herState.set((prevState) => ({
		...prevState,
		rovoMessage,
	}));
};

export const clearRovoMessage = (): void => {
	herState.set((prevState) => ({
		...prevState,
		rovoMessage: null,
	}));
};
